import { useState } from "react";
import { Clock, Pencil, X, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { addDays, daysBetween } from "@/components/FestivalDetailsStep";

const pad = (n) => String(n).padStart(2, "0");
export const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const datePart = (v) => (v || "").slice(0, 10);
const timePart = (v) => (v || "").slice(11, 16) || "18:00";

const EDITABLE_STATES = new Set(["draft", "demo_active", "demo_expired"]);
const inputCls = "w-full mt-1 bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-sm text-white focus:outline-none focus:border-[#CCFF00] [color-scheme:dark]";
const labelCls = "text-[10px] uppercase tracking-widest text-[#A3A3A3]";

export function EventWindowCard({ festival, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ start: "", end: "" });
  const [busy, setBusy] = useState(false);
  const canEdit = EDITABLE_STATES.has(festival.license?.state);
  const days = daysBetween(datePart(form.start), datePart(form.end));

  const open = () => {
    setForm({ start: toLocalInput(festival.start_at), end: toLocalInput(festival.end_at) });
    setEditing(true);
  };
  const setStart = (v) => setForm((f) => {
    const keep = daysBetween(datePart(f.start), datePart(f.end)) || 1;
    return { start: v, end: v ? `${addDays(datePart(v), keep - 1)}T${timePart(f.end)}` : f.end };
  });
  const setDays = (n) => {
    const d = Math.max(1, Math.min(14, Number(n) || 1));
    setForm((f) => ({ ...f, end: f.start ? `${addDays(datePart(f.start), d - 1)}T${timePart(f.end)}` : f.end }));
  };

  const error = !form.start || !form.end ? "Pick a start and end"
    : new Date(form.start) <= new Date() ? "Start must be in the future"
    : new Date(form.end) <= new Date(form.start) ? "End must be after the start"
    : null;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/festival/${festival.id}`, {
        start_at: new Date(form.start).toISOString(),
        end_at: new Date(form.end).toISOString(),
      });
      toast.success("Event window updated");
      setEditing(false);
      await onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e, "Failed to save"));
    } finally { setBusy(false); }
  };

  return (
    <div className="card-surface p-6 mb-4" data-testid="festival-window-card">
      <div className="flex items-center justify-between mb-2">
        <div className="label-mono text-[#CCFF00]">EVENT WINDOW</div>
        {canEdit && !editing && (
          <button type="button" onClick={open} data-testid="festival-window-edit"
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#A3A3A3] hover:text-[#CCFF00] transition-colors">
            <Pencil className="w-3 h-3" /> Edit
          </button>
        )}
      </div>
      {!editing ? (
        <div className="text-sm text-[#E5E5E5] flex items-center gap-2 flex-wrap" data-testid="festival-window-display">
          <Clock className="w-4 h-4 text-[#A3A3A3]" />
          {festival.start_at ? new Date(festival.start_at).toLocaleString() : "—"} → {festival.end_at ? new Date(festival.end_at).toLocaleString() : "—"}
          {festival.planned_days ? <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">· {festival.planned_days} day{festival.planned_days === 1 ? "" : "s"}</span> : null}
        </div>
      ) : (
        <div className="space-y-3" data-testid="festival-window-form">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className={labelCls}>Start</label>
              <input type="datetime-local" value={form.start} onChange={(e) => setStart(e.target.value)} data-testid="festival-window-start" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>End</label>
              <input type="datetime-local" value={form.end} min={form.start} onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))} data-testid="festival-window-end" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Day count</label>
              <input type="number" min={1} max={14} value={days && days > 0 ? days : ""} onChange={(e) => setDays(e.target.value)} data-testid="festival-window-days" className={inputCls} />
            </div>
          </div>
          <p className="text-[10px] text-[#8A8A8A]">Changing the dates or day count updates the pass prices below once saved.</p>
          {error && <div className="text-[11px] text-[#FFB020]" data-testid="festival-window-error">{error}</div>}
          <div className="flex gap-2">
            <button type="button" onClick={save} disabled={busy || !!error} data-testid="festival-window-save"
              className="inline-flex items-center gap-1 px-4 py-2 text-[11px] uppercase tracking-widest font-bold bg-[#CCFF00] text-black hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
              <Check className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}
            </button>
            <button type="button" onClick={() => setEditing(false)} disabled={busy} data-testid="festival-window-cancel"
              className="inline-flex items-center gap-1 px-4 py-2 text-[11px] uppercase tracking-widest border border-[#525252] text-[#A3A3A3] hover:text-white hover:border-white">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
